import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import fnoService, { FnoAssessment, FuturesUnderlying } from '../services/fno.service';

interface FnoState {
  underlyings: FuturesUnderlying[];
  assessments: Record<number, FnoAssessment>;  // keyed by alertId
  loading: boolean;
  error: string | null;
}

const initialState: FnoState = {
  underlyings: [],
  assessments: {},
  loading: false,
  error: null
};

export const fetchFnoUnderlyings = createAsyncThunk(
  'fno/fetchUnderlyings',
  async () => fnoService.getUnderlyings()
);

export const fetchAssessmentsBatch = createAsyncThunk(
  'fno/fetchAssessmentsBatch',
  async (alertIds: number[]) => fnoService.getAssessmentsBatch(alertIds)
);

const fnoSlice = createSlice({
  name: 'fno',
  initialState,
  reducers: {
    // Called by WebSocket handler when fno:assessment event arrives
    upsertAssessment(state, action: PayloadAction<FnoAssessment>) {
      state.assessments[action.payload.alertId] = action.payload;
    },
    // Merge a batch of assessments
    mergeAssessments(state, action: PayloadAction<Record<number, FnoAssessment>>) {
      Object.assign(state.assessments, action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFnoUnderlyings.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchFnoUnderlyings.fulfilled, (state, action) => {
        state.loading = false;
        state.underlyings = action.payload;
      })
      .addCase(fetchFnoUnderlyings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch FNO underlyings';
      })
      .addCase(fetchAssessmentsBatch.fulfilled, (state, action) => {
        Object.assign(state.assessments, action.payload);
      });
  }
});

export const { upsertAssessment, mergeAssessments } = fnoSlice.actions;
export default fnoSlice.reducer;
